import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Phone, Search, Plus, Globe, Briefcase, Users, BadgeCheck, Mail } from "lucide-react";
import { toast } from "sonner";
import { Layout, PageHeader } from "../components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const IN = "w-full bg-white border border-eon-border rounded-lg px-3 py-2.5 text-sm text-eon-text focus:border-eon-gold outline-none";

function ListBusinessDialog({ type, categories, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", category: categories[0] || "", location: "", description: "", phone: "", email: "", website: "" });
  const save = async () => {
    if (!f.name || !f.category) { toast.error("Name and category are required"); return; }
    try {
      await api.post("/services", { ...f, service_type: type });
      toast.success("Submitted for review — we'll approve it shortly.");
      setOpen(false); setF({ name: "", category: categories[0] || "", location: "", description: "", phone: "", email: "", website: "" });
      onDone?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Could not submit"); }
  };
  if (!user) {
    return <Link to="/login" data-testid="marketplace-list-signin" className="inline-flex items-center gap-2 rounded-full bg-eon-gold text-white text-sm font-bold px-5 py-2.5 hover:bg-eon-goldhover transition-colors"><Plus size={16} /> List Your Business</Link>;
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="marketplace-list-btn" className="inline-flex items-center gap-2 rounded-full bg-eon-gold text-white text-sm font-bold px-5 py-2.5 hover:bg-eon-goldhover transition-colors"><Plus size={16} /> List Your Business</button>
      </DialogTrigger>
      <DialogContent className="bg-white text-eon-text max-h-[88vh] overflow-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">List on the E.O.N Marketplace</DialogTitle></DialogHeader>
        <p className="text-xs text-eon-muted -mt-2">Submit your {type === "business" ? "business service" : "service"} for review. Approved listings appear in the marketplace.</p>
        <div className="space-y-3 pt-2">
          <input placeholder="Business / service name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={IN} data-testid="list-name" />
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={IN} data-testid="list-category">
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Location (City, State)" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={IN} data-testid="list-location" />
            <input placeholder="Website (optional)" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className={IN} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={IN} />
            <input placeholder="Contact email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={IN} />
          </div>
          <textarea rows={3} placeholder="Describe your service" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={IN} data-testid="list-description" />
          <button onClick={save} className="w-full rounded-full bg-eon-gold text-white font-bold py-3 hover:bg-eon-goldhover transition-colors" data-testid="list-submit">Submit for Review</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ContactProviderDialog({ service }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", message: `I'm interested in ${service.name}.` });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/services/${service.id}/lead`, f); setSent(true); toast.success("Message sent to the provider"); }
    catch { toast.error("Could not send message"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`contact-provider-${service.id}`} className="mt-4 w-full rounded-full bg-eon-gold text-white text-sm font-bold py-2.5 hover:bg-eon-goldhover transition-colors">Contact</button>
      </DialogTrigger>
      <DialogContent className="bg-white text-eon-text">
        <DialogHeader><DialogTitle className="font-display text-2xl">Contact {service.name}</DialogTitle></DialogHeader>
        {sent ? (
          <div className="py-8 text-center"><Mail size={36} className="text-eon-gold mx-auto" /><p className="mt-3 text-eon-muted">Your message was sent to the provider.</p></div>
        ) : (
          <form onSubmit={submit} className="space-y-3 pt-2" data-testid={`lead-form-${service.id}`}>
            <input required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={IN} data-testid="lead-name" />
            <input required type="email" placeholder="Your email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={IN} data-testid="lead-email" />
            <input placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={IN} />
            <textarea required rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={IN} data-testid="lead-message" />
            <button type="submit" className="w-full rounded-full bg-eon-gold text-white font-bold py-3 hover:bg-eon-goldhover transition-colors" data-testid="lead-submit">Send Message</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Services() {
  const [type, setType] = useState("consumer");
  const [cats, setCats] = useState({ consumer: [], business: [] });
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [services, setServices] = useState([]);

  useEffect(() => { api.get("/service-categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const qs = new URLSearchParams({ service_type: type });
    if (cat) qs.append("category", cat);
    if (q) qs.append("q", q);
    api.get(`/services?${qs.toString()}`).then((r) => setServices(r.data)).catch(() => setServices([]));
  }, [type, cat, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const categories = cats[type] || [];

  return (
    <Layout>
      <PageHeader label="E.O.N Marketplace" title="Built Around the Boerboel Community"
        subtitle="Find trusted providers and businesses — or list your own. From breeders and trainers to the business services that power the industry." />
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10">
        {/* type toggle + list CTA */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="inline-flex rounded-full border border-eon-border p-1 bg-white">
            <button onClick={() => { setType("consumer"); setCat(""); }} data-testid="marketplace-tab-consumer"
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors ${type === "consumer" ? "bg-eon-gold text-white" : "text-eon-muted"}`}><Users size={16} /> Services</button>
            <button onClick={() => { setType("business"); setCat(""); }} data-testid="marketplace-tab-business"
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors ${type === "business" ? "bg-eon-gold text-white" : "text-eon-muted"}`}><Briefcase size={16} /> Business (B2B)</button>
          </div>
          <ListBusinessDialog type={type} categories={categories} onDone={load} />
        </div>

        {/* search */}
        <div className="flex items-center gap-3 bg-white border border-eon-border rounded-full px-5 mb-5">
          <Search size={18} className="text-eon-gold" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="What are you looking for?" data-testid="marketplace-search"
            className="flex-1 bg-transparent py-3 text-sm text-eon-text outline-none" />
        </div>

        {/* categories */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button onClick={() => setCat("")} className={`text-xs font-bold rounded-full px-4 py-2 transition-colors ${cat === "" ? "bg-eon-gold text-white" : "bg-white border border-eon-border text-eon-muted hover:border-eon-gold"}`}>All</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`marketplace-cat-${c.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              className={`text-xs font-bold rounded-full px-4 py-2 transition-colors ${cat === c ? "bg-eon-gold text-white" : "bg-white border border-eon-border text-eon-muted hover:border-eon-gold"}`}>{c}</button>
          ))}
        </div>

        {services.length === 0 ? (
          <div className="text-center py-20 rounded-2xl border border-dashed border-eon-border bg-white" data-testid="marketplace-empty">
            <p className="text-eon-text font-display font-bold text-lg">No providers listed here yet.</p>
            <p className="text-sm text-eon-muted mt-2">Be the first — list your {type === "business" ? "business service" : "service"} and reach the E.O.N community.</p>
            <div className="mt-5 flex justify-center"><ListBusinessDialog type={type} categories={categories} onDone={load} /></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <div key={s.id} data-testid={`service-card-${s.id}`} className="eon-card overflow-hidden animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                {s.image && <div className="relative aspect-[16/10] overflow-hidden"><img src={s.image} alt={s.name} className="w-full h-full object-cover" /><span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-widest bg-white/90 text-eon-gold rounded-full px-3 py-1">{s.category}</span></div>}
                <div className="p-6">
                  {!s.image && <div className="eon-label mb-1">{s.category}</div>}
                  <h3 className="font-display text-xl font-bold text-eon-text flex items-center gap-1.5">{s.name}{s.verified && <BadgeCheck size={16} className="text-eon-gold" data-testid={`verified-${s.id}`} />}</h3>
                  <div className="flex items-center gap-3 mt-2 text-xs text-eon-muted">
                    {s.location && <span className="flex items-center gap-1"><MapPin size={12} /> {s.location}</span>}
                    {s.rating > 0 && <span className="flex items-center gap-1"><Star size={12} className="text-eon-gold fill-eon-gold" /> {s.rating}</span>}
                    {s.verified && <span className="text-eon-gold font-semibold">E.O.N Verified</span>}
                  </div>
                  <p className="text-sm text-eon-muted mt-3 line-clamp-2">{s.description}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-eon-muted">
                    {s.phone && <span className="flex items-center gap-1"><Phone size={12} /> {s.phone}</span>}
                    {s.website && <a href={s.website.startsWith("http") ? s.website : `https://${s.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-eon-gold"><Globe size={12} /> Website</a>}
                  </div>
                  <ContactProviderDialog service={s} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
